/**
 * Print a PDF blob without a pop-up.
 *
 * `window.open(url, '_blank', 'noopener,noreferrer')` returns null to the
 * opener by specification, so a caller that reads the null as "pop-up blocked"
 * reports a blocked pop-up on every click and downloads the file instead. A
 * same-origin blob URL in an offscreen iframe prints with no pop-up involved;
 * if the engine refuses to print a PDF that way, `onFallback` runs so the
 * caller can save the file and the action never dead-ends. Same approach as
 * ExportIcons' print.
 */
export function printPdfBlob(
  blob: Blob,
  { onPrinted, onFallback }: { onPrinted?: () => void; onFallback: () => void },
): void {
  const url = URL.createObjectURL(blob)
  const frame = document.createElement('iframe')
  frame.setAttribute('aria-hidden', 'true')
  frame.setAttribute('data-print-frame', 'true')
  Object.assign(frame.style, {
    position: 'fixed',
    right: '0',
    bottom: '0',
    width: '1px',
    height: '1px',
    border: '0',
    opacity: '0',
    pointerEvents: 'none',
  })

  let settled = false
  const fallback = () => {
    if (settled) return
    settled = true
    frame.remove()
    URL.revokeObjectURL(url)
    onFallback()
  }

  frame.onload = () => {
    if (settled) return
    const win = frame.contentWindow
    if (!win) {
      fallback()
      return
    }
    try {
      win.focus()
      win.print()
      settled = true
      onPrinted?.()
      window.setTimeout(() => {
        frame.remove()
        URL.revokeObjectURL(url)
      }, 60_000)
    } catch {
      fallback()
    }
  }
  frame.onerror = fallback
  // Backstop: some engines neither load nor error on a PDF in an iframe.
  window.setTimeout(fallback, 4_000)

  document.body.appendChild(frame)
  frame.src = url
}
